import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalQuizzes: 0,
    activeQuizzes: 0,
    totalAttempts: 0,
    pendingReview: 0
  });
  const [recentQuizzes, setRecentQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/quizzes');
      const quizzes = response.data;

      const totalQuizzes = quizzes.length;
      const activeQuizzes = quizzes.filter(q => q.is_active).length;
      const totalAttempts = quizzes.reduce((sum, q) => sum + parseInt(q.attempt_count || 0), 0);

      setStats({
        totalQuizzes,
        activeQuizzes,
        totalAttempts,
        pendingReview: 0 // Would need separate API call
      });

      setRecentQuizzes(quizzes.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  return (
    <div>
      <div className="flex flex-between flex-center mb-5">
        <div>
          <h1>Welcome, {user?.name}!</h1>
          <p className="text-muted">Here's an overview of your quiz platform</p>
        </div>
        <Link to="/quizzes/new" className="btn btn-primary">
          + Create New Quiz
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-3 mb-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        <div className="card">
          <p className="text-muted text-sm">Total Quizzes</p>
          <h2 style={{ fontSize: '36px', color: '#f97316' }}>{stats.totalQuizzes}</h2>
        </div>
        <div className="card">
          <p className="text-muted text-sm">Active Quizzes</p>
          <h2 style={{ fontSize: '36px', color: '#10b981' }}>{stats.activeQuizzes}</h2>
        </div>
        <div className="card">
          <p className="text-muted text-sm">Total Attempts</p>
          <h2 style={{ fontSize: '36px', color: '#f59e0b' }}>{stats.totalAttempts}</h2>
        </div>
      </div>

      {/* Recent Quizzes */}
      <div className="card">
        <div className="flex flex-between flex-center mb-4">
          <h3>Recent Quizzes</h3>
          <Link to="/quizzes" className="btn btn-secondary btn-sm">View All</Link>
        </div>

        {recentQuizzes.length === 0 ? (
          <div className="empty-state">
            <h3>No quizzes yet</h3>
            <p>Create your first quiz to get started</p>
            <Link to="/quizzes/new" className="btn btn-primary mt-3">Create Quiz</Link>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Access Code</th>
                <th>Questions</th>
                <th>Attempts</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {recentQuizzes.map(quiz => (
                <tr key={quiz.id}>
                  <td>{quiz.title}</td>
                  <td>
                    <code style={{ background: '#f3f4f6', padding: '4px 8px', borderRadius: '4px' }}>
                      {quiz.access_code}
                    </code>
                  </td>
                  <td>{quiz.question_count || 0}</td>
                  <td>{quiz.attempt_count || 0}</td>
                  <td>
                    <span className={`badge ${quiz.is_active ? 'badge-success' : 'badge-danger'}`}>
                      {quiz.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <Link to={`/quizzes/${quiz.id}`} className="btn btn-secondary btn-sm">Edit</Link>
                      <Link to={`/quizzes/${quiz.id}/results`} className="btn btn-outline btn-sm">Results</Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Quick Access Link */}
      <div className="card mt-4">
        <h3 className="mb-2">Share Exam Link</h3>
        <p className="text-muted mb-3">
          Candidates can access exams at this URL with their access code:
        </p>
        <code style={{
          display: 'block',
          background: '#f3f4f6',
          padding: '12px',
          borderRadius: '8px',
          wordBreak: 'break-all'
        }}>
          {window.location.origin}/exam
        </code>
      </div>
    </div>
  );
};

export default Dashboard;
